/**
 * Bundle Engine — client-side behavior.
 *
 * 1. <bundle-form> adds every selected slot variant to the cart in a single request, tagging each
 *    line with a shared `_bundle_id` / `_bundle_name` property. It mirrors the theme's native
 *    <product-form> cart integration (same fetch endpoint, same cart:change event) so the cart
 *    drawer, cart count, etc. all update exactly as they do for a normal add-to-cart.
 * 2. A document-level listener cascades line removal: when any cart line carrying a `_bundle_id`
 *    is removed, every other line sharing that id is removed too.
 */

class BundleForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector("form");
    this.form?.addEventListener("submit", this.onSubmit.bind(this));
    this.form?.addEventListener("change", this.onSlotChange.bind(this));

    // Pattern B slots with more than one option (e.g. Ausführung + Farbe) start out cascade-filtered too,
    // in case a whole option value has no stock left in any combination.
    this.querySelectorAll("[data-bundle-slot]").forEach((slot) => {
      if (slot.querySelector("script[data-bundle-variants]")) {
        this.cascadeSlotOptions(slot);
      }
    });
  }

  onSlotChange(event) {
    const select = event.target.closest("[data-bundle-slot] select");
    if (!select) return;

    const slot = select.closest("[data-bundle-slot]");
    if (slot.querySelector("script[data-bundle-variants]")) {
      this.cascadeSlotOptions(slot);
    }
    this.resolveSlot(slot);
  }

  /**
   * For a Pattern B slot with multiple options (e.g. Ausführung + Farbe), narrow each option's dropdown down
   * to the values that actually exist given the options already chosen before it — mirroring how the theme's
   * native variant picker only offers real combinations. A value that exists for the current prefix but is out
   * of stock stays visible, disabled, and labeled "(ausverkauft)"; a value that doesn't exist at all for the
   * current prefix is removed from the dropdown entirely.
   */
  cascadeSlotOptions(slot) {
    const variantsScript = slot.querySelector("script[data-bundle-variants]");
    const variants = JSON.parse(variantsScript.textContent);
    const selects = Array.from(slot.querySelectorAll("select"));

    selects.forEach((select) => {
      select._bundleAllOptions ??= Array.from(select.options).map((option) => option.cloneNode(true));
    });

    selects.forEach((select, index) => {
      const prefixValues = selects.slice(0, index).map((s) => s.value);
      const matchingVariants = variants.filter((variant) =>
        prefixValues.every((value, i) => variant.options[i] === value)
      );

      const validValues = new Set(matchingVariants.map((variant) => variant.options[index]));
      const availableValues = new Set(
        matchingVariants.filter((variant) => variant.available).map((variant) => variant.options[index])
      );

      const currentValue = select.value;

      const keptOptions = select._bundleAllOptions
        .filter((option) => option.value === "" || validValues.has(option.value))
        .map((option) => {
          const clone = option.cloneNode(true);
          if (clone.value !== "" && !availableValues.has(clone.value)) {
            clone.disabled = true;
            if (!clone.textContent.includes("ausverkauft")) {
              clone.textContent = `${clone.textContent} (ausverkauft)`;
            }
          }
          return clone;
        });

      select.replaceChildren(...keptOptions);
      select.value = validValues.has(currentValue) ? currentValue : "";
    });
  }

  /**
   * Determine which variant (if any) the slot's current selections resolve to, and reflect that in the
   * thumbnail / unavailable-combination hint / `data-resolved-variant-id`. Two cases:
   *  - Pattern A: a single select per slot whose OWN value already is the variant id (see bundle-picker-a.liquid).
   *  - Pattern B: one or more selects per slot; the slot embeds the product's full variant list as JSON, and the
   *    matching variant must be looked up from the current combination of select values (see bundle-picker-b.liquid).
   */
  resolveSlot(slot) {
    const thumb = slot.querySelector("[data-bundle-thumb]");
    const unavailableEl = slot.querySelector("[data-bundle-unavailable]");
    const variantsScript = slot.querySelector("script[data-bundle-variants]");

    let resolvedId = null;
    let resolvedThumb = null;
    let resolvedThumbLarge = null;

    if (variantsScript) {
      const selects = Array.from(slot.querySelectorAll("select"));
      const values = selects.map((select) => select.value);
      const allFilled = values.length > 0 && values.every((value) => value !== "");

      if (allFilled) {
        const variants = JSON.parse(variantsScript.textContent);
        const match = variants.find(
          (variant) =>
            variant.available &&
            variant.options.length === values.length &&
            variant.options.every((value, index) => value === values[index])
        );
        if (match) {
          resolvedId = match.id;
          resolvedThumb = match.image;
          resolvedThumbLarge = match.imageLarge;
        }
      }

      if (unavailableEl) unavailableEl.hidden = !(allFilled && !resolvedId);
    } else {
      const select = slot.querySelector("select");
      if (select?.value) {
        resolvedId = parseInt(select.value, 10);
        resolvedThumb = select.selectedOptions[0]?.dataset.thumb;
        resolvedThumbLarge = select.selectedOptions[0]?.dataset.thumbLarge;
      }
    }

    if (resolvedId) {
      slot.dataset.resolvedVariantId = resolvedId;
    } else {
      delete slot.dataset.resolvedVariantId;
    }

    if (thumb) {
      // The popover can be reparented to <body> once opened (for stacking), so look it up by id — a plain
      // descendant query would stop finding it after that first move.
      const popoverId = thumb.getAttribute("aria-controls");
      const largeImg = popoverId
        ? document.getElementById(popoverId)?.querySelector("[data-bundle-thumb-large]")
        : null;
      if (resolvedThumb) {
        thumb.querySelector("img").src = resolvedThumb;
        if (largeImg) largeImg.src = resolvedThumbLarge || resolvedThumb;
        thumb.hidden = false;
      } else {
        thumb.hidden = true;
      }
    }
  }

  async onSubmit(event) {
    event.preventDefault();

    if (!this.form.checkValidity()) {
      this.form.reportValidity();
      return;
    }

    const submitButton = this.form.querySelector('button[type="submit"]');
    if (submitButton?.getAttribute("aria-busy") === "true") return;

    const slots = Array.from(this.querySelectorAll("[data-bundle-slot]"));
    const items = slots.map((slot) => {
      const id = slot.dataset.resolvedVariantId;
      return id ? parseInt(id, 10) : null;
    });

    if (items.some((id) => !id)) return;

    const giftEl = this.querySelector("[data-bundle-gift]");
    if (giftEl?.dataset.variantId) {
      items.push(parseInt(giftEl.dataset.variantId, 10));
    }

    submitButton?.setAttribute("aria-busy", "true");

    const bundleId = `${this.getAttribute("bundle-id")}-${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
    const bundleName = this.getAttribute("bundle-name") || "";

    const cartItems = items.map((variantId) => ({
      id: variantId,
      quantity: 1,
      properties: {
        _bundle_id: bundleId,
        _bundle_name: bundleName
      }
    }));

    const sectionsToBundle = new Set();
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    try {
      const response = await fetch(`${Shopify.routes.root}cart/add.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest"
        },
        body: JSON.stringify({ items: cartItems, sections: [...sectionsToBundle].join(",") })
      });

      const responseJson = await response.json();

      if (!response.ok) {
        throw new Error(responseJson.message || responseJson.description || "Cart error");
      }

      const cartContent = await (await fetch(`${Shopify.routes.root}cart.js`)).json();
      cartContent.sections = responseJson.sections;

      document.documentElement.dispatchEvent(
        new CustomEvent("cart:change", { bubbles: true, detail: { baseEvent: "variant:add", cart: cartContent } })
      );
    } catch (error) {
      document.dispatchEvent(new CustomEvent("toast:show", { detail: { message: error.message, tone: "error" } }));
      document.documentElement.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
    } finally {
      submitButton?.removeAttribute("aria-busy");
    }
  }
}

if (!window.customElements.get("bundle-form")) {
  window.customElements.define("bundle-form", BundleForm);
}

if (!window.__bundleEngineCascadeInit) {
  window.__bundleEngineCascadeInit = true;

  document.addEventListener("line-item:change", async (event) => {
    if (event.detail?.quantity !== 0) return;

    const removedLineItem = event.target.closest("line-item");
    const bundleId = removedLineItem?.getAttribute("data-bundle-id");
    if (!bundleId) return;

    const siblingLineItems = Array.from(
      document.querySelectorAll(`line-item[data-bundle-id="${CSS.escape(bundleId)}"]`)
    ).filter((lineItem) => lineItem !== removedLineItem);
    const updates = {};
    siblingLineItems.forEach((lineItem) => {
      const key = lineItem.getAttribute("data-line-key");
      if (key) updates[key] = 0;
    });

    if (Object.keys(updates).length === 0) return;

    const sectionsToBundle = new Set();
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:prepare-bundled-sections", { bubbles: true, detail: { sections: sectionsToBundle } })
    );

    const response = await fetch(`${Shopify.routes.root}cart/update.js`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ updates, sections: [...sectionsToBundle].join(",") })
    });

    if (!response.ok) return;

    const cartContent = await response.json();

    document.documentElement.dispatchEvent(
      new CustomEvent("cart:change", { bubbles: true, detail: { baseEvent: "line-item:change", cart: cartContent } })
    );
  });
}
